import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import api, { authAPI, accountAPI } from '../../services/api';
import { useMerchantPayoutFlags } from '../../hooks/useMerchantPayoutFlags';
import LogoutModal from '../../components/LogoutModal';
import WalletCreditsModal from '../../components/WalletCreditsModal';
import Footer from '../../components/Footer';

interface MerchantAccountData {
  name: string;
  mobile: string;
  email: string;
  businessName: string;
  appUrl: string;
  acceptingPayments: string;
  ipAddress: string;
  businessType: string;
  services: string;
  registerDate: string;
  apiId: string;
  apiKey: string;
  secretKey: string;
  webhookUrl: string;
}

const MerchantAccount: React.FC = () => {
  const { user, logout } = useAuth();
  const { viyonaEnabled, poolpayEnabled } = useMerchantPayoutFlags();
  const [accountData, setAccountData] = useState<MerchantAccountData>({
    name: '',
    mobile: '',
    email: '',
    businessName: '',
    appUrl: '',
    acceptingPayments: '',
    ipAddress: '',
    businessType: '',
    services: '',
    registerDate: '',
    apiId: '',
    apiKey: '',
    secretKey: '',
    webhookUrl: ''
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('account');
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(() => typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches);

  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [tpinLoading, setTpinLoading] = useState(false);
  const [tpinMessage, setTpinMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [apiKeysSyncLoading, setApiKeysSyncLoading] = useState(false);
  const [apiKeysSyncMessage, setApiKeysSyncMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const handler = () => setIsDesktop(mq.matches);
    mq.addEventListener('change', handler);
    setIsDesktop(mq.matches);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    fetchMerchantAccountData();
    
    // Fallback timeout to ensure loading doesn't get stuck
    const timeout = setTimeout(() => {
      if (loading) {
        console.log('Loading timeout reached, setting loading to false');
        setLoading(false);
      }
    }, 10000); // 10 second timeout
    
    return () => clearTimeout(timeout);
  }, []);

  const fetchMerchantAccountData = async () => {
    try {
      setLoading(true);
      console.log('Fetching merchant account data...');
      console.log('API Base URL:', process.env.REACT_APP_API_URL || 'http://localhost:3000/api/v1');
      console.log('Token:', localStorage.getItem('token'));
      
      // Fetch user profile data
      const profileResponse = await api.get('/auth/profile');
      console.log('Profile response:', profileResponse.data);
      const profileData = profileResponse.data.data;
      
      // Update account data with real user data - all fields are now dynamic!
      const newAccountData = {
        name: profileData.name || 'N/A',
        email: profileData.email || 'N/A',
        mobile: profileData.phone || 'N/A',
        businessName: profileData.business_name || 'N/A',
        appUrl: profileData.app_url || 'N/A',
        acceptingPayments: profileData.accepting_payments || 'N/A',
        ipAddress: profileData.ip_address || 'N/A',
        businessType: profileData.business_type || 'N/A',
        services: profileData.services || 'PG Domestic | UPI Collections',
        registerDate: profileData.created_at ? new Date(profileData.created_at).toLocaleString() : 'N/A',
        apiId: profileData.client_id || 'N/A',
        apiKey: profileData.api_key || 'Generating...',
        secretKey: profileData.client_secret || 'Generating...',
        webhookUrl: profileData.webhook_url || 'Not configured'
      };
      
      console.log('Setting account data:', newAccountData);
      setAccountData(newAccountData);
    } catch (error: any) {
      console.error('Error fetching merchant account data:', error);
      console.error('Error details:', error.response?.data || error.message);
      
      // Set some default data so the page doesn't stay blank
      setAccountData({
        name: user?.name || 'Merchant User',
        email: user?.email || 'merchant@example.com',
        mobile: '+91 9876543210',
        businessName: 'My Business',
        appUrl: 'https://myapp.com',
        acceptingPayments: 'Yes',
        ipAddress: '192.168.1.1',
        businessType: 'E-commerce',
        services: 'PG Domestic | UPI Collections',
        registerDate: new Date().toLocaleString(),
        apiId: 'N/A',
        apiKey: 'API_KEY_NOT_AVAILABLE',
        secretKey: 'SECRET_KEY_NOT_AVAILABLE',
        webhookUrl: 'https://myapp.com/webhook'
      });
    } finally {
      setLoading(false);
      console.log('Loading set to false');
    }
  };

  const merchantSidebarItems = [
    { id: 'home', label: 'Home', icon: '🏠' },
    { id: 'account', label: 'My Account', icon: '🏦' },
    { id: 'payouts', label: 'Payouts', icon: '📋' },
    { id: 'reports', label: 'Reports', icon: '📊' },
    ...(viyonaEnabled ? [{ id: 'create-payout' as const, label: 'Create Payout (v1 Viyona)', icon: '➕' }] : []),
    ...(poolpayEnabled ? [{ id: 'create-payout-v2' as const, label: 'Create Payout (v2 PoolPay)', icon: '💠' }] : []),
    { id: 'logout', label: 'Logout', icon: '🚪' }
  ];

  const handleLogout = async () => {
    try {
      await logout();
      setShowLogoutModal(false);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleLogoutClick = () => {
    setShowLogoutModal(true);
  };

  const handleCreditMoney = () => {
    setShowCreditsModal(true);
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleAddPayout = () => {
    console.log('Add Payout clicked');
    window.location.href = '/merchant/new-payouts';
  };

  const handleMyProfile = () => {
    window.location.href = '/merchant/account';
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordMessage(null);
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordMessage({ type: 'error', text: 'New password and confirm password do not match.' });
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      setPasswordMessage({ type: 'error', text: 'New password must be at least 6 characters long.' });
      return;
    }
    setPasswordLoading(true);
    try {
      await authAPI.changePassword(passwordForm.currentPassword, passwordForm.newPassword);
      setPasswordMessage({ type: 'success', text: 'Password changed successfully!' });
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err: any) {
      setPasswordMessage({ type: 'error', text: err.response?.data?.message || 'Failed to change password. Please check your current password.' });
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleResetTpin = async () => {
    setTpinMessage(null);
    setTpinLoading(true);
    try {
      const res = await api.post('/merchants/send-tpin', { forceRegenerate: true });
      if (res.data.success) {
        setTpinMessage({ type: 'success', text: res.data.message || 'New TPIN sent to your registered mobile number. Valid for 15 days.' });
      } else {
        setTpinMessage({ type: 'error', text: res.data.message || 'Failed to reset TPIN.' });
      }
    } catch (err: any) {
      setTpinMessage({ type: 'error', text: err.response?.data?.message || 'Failed to reset TPIN. Please ensure your mobile number is updated in your profile.' });
    } finally {
      setTpinLoading(false);
    }
  };

  const handleSyncApiKeysFromManager = async () => {
    setApiKeysSyncMessage(null);
    setApiKeysSyncLoading(true);
    try {
      const res = await accountAPI.syncApiKeysFromManager();
      if (res.data.success) {
        setApiKeysSyncMessage({ type: 'success', text: res.data.message || 'API keys updated successfully from your manager.' });
        await fetchMerchantAccountData();
      } else {
        setApiKeysSyncMessage({ type: 'error', text: res.data.message || 'Failed to sync API keys.' });
      }
    } catch (err: any) {
      setApiKeysSyncMessage({ type: 'error', text: err.response?.data?.message || 'Failed to sync API keys. Please ensure you have a manager assigned.' });
    } finally {
      setApiKeysSyncLoading(false);
    }
  };

  const handleNavigation = (tabId: string) => {
    setActiveTab(tabId);
    if (tabId === 'home') {
      window.location.href = '/merchant';
    } else if (tabId === 'payouts') {
      window.location.href = '/merchant/payouts';
    } else if (tabId === 'reports') {
      window.location.href = '/merchant/reports';
    } else if (tabId === 'create-payout') {
      window.location.href = '/merchant/new-payouts';
    } else if (tabId === 'create-payout-v2') {
      window.location.href = '/merchant/new-payouts/poolpay';
    } else if (tabId === 'logout') {
      setShowLogoutModal(true);
    }
    // For 'account', stay on current page (/merchant/account)
    // Close sidebar on mobile after navigation
    setSidebarOpen(false);
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-slate-100 text-violet-600">
        <div className="w-12 h-12 border-4 border-slate-200 border-t-violet-500 rounded-full animate-spin mb-5" />
        <p className="text-violet-600 text-base m-0">Loading Account Information...</p>
        <p className="text-slate-500 text-sm mt-2 m-0">If this takes too long, please check your connection and try refreshing the page.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen w-full overflow-x-hidden bg-gradient-to-br from-slate-100 to-slate-200">
      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 md:hidden" onClick={() => setSidebarOpen(false)} aria-hidden />}
      <aside
        id="merchant-sidebar"
        className="fixed top-0 left-0 z-50 h-full w-64 bg-[#373a63] text-slate-100 shadow-xl transition-transform duration-200 ease-out"
        style={isDesktop ? { transform: 'translateX(0)' } : sidebarOpen ? { transform: 'translateX(0)' } : { transform: 'translateX(-100%)' }}
      >
        <div className="p-5 border-b border-slate-600">
          <div className="flex items-center gap-2">
            <span className="text-2xl">💳</span>
            <span className="font-bold text-lg">YuvanAPI</span>
          </div>
        </div>
        <nav className="p-4 flex flex-col gap-1">
          {merchantSidebarItems.map(item => (
            <button key={item.id} type="button" className={`flex items-center gap-3 w-full py-3 px-4 rounded-lg text-left font-medium transition-colors ${activeTab === item.id ? 'bg-violet-600 text-white' : 'text-slate-300 hover:bg-slate-700 hover:text-white'}`} onClick={() => handleNavigation(item.id)}>
              <span className="text-xl">{item.icon}</span>
              <span className="leading-tight">
                {item.id === 'create-payout' ? (
                  <>
                    <span className="block">Create Payout</span>
                    <span className="block text-xs opacity-90">(v1 Viyona)</span>
                  </>
                ) : item.id === 'create-payout-v2' ? (
                  <>
                    <span className="block">Create Payout</span>
                    <span className="block text-xs opacity-90">(v2 PoolPay)</span>
                  </>
                ) : (
                  <span className="block">{item.label}</span>
                )}
              </span>
            </button>
          ))}
        </nav>
      </aside>

      <header className="flex items-center gap-4 p-4 bg-[#373a63] text-white shadow sticky top-0 z-30" style={isDesktop ? { display: 'none' } : undefined}>
        <button type="button" className="p-2 rounded-lg hover:bg-slate-700" onClick={toggleSidebar} aria-label="Menu">☰</button>
        <h1 className="text-lg font-bold m-0">YuvanAPI</h1>
      </header>

      <main id="merchant-main" className="flex-1 p-6 pt-8" style={isDesktop ? { marginLeft: '16rem' } : undefined}>
        <div
          className="flex flex-col gap-4 mb-6"
          style={isDesktop ? { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' } : undefined}
        >
          <h1 className="text-2xl font-bold text-slate-800 m-0 shrink-0">Welcome to YuvanAPI Merchant!</h1>
          <div className="flex flex-wrap gap-2" style={{ flexShrink: 0 }}>
            <button type="button" onClick={handleCreditMoney} className="flex items-center gap-2 py-2.5 px-4 rounded-lg bg-blue-600 text-white text-sm font-semibold border-none cursor-pointer hover:bg-blue-700 whitespace-nowrap shrink-0">💳 CREDIT MONEY</button>
            <button type="button" onClick={handleAddPayout} className="flex items-center gap-2 py-2.5 px-4 rounded-lg bg-violet-600 text-white text-sm font-semibold border-none cursor-pointer hover:bg-violet-700 whitespace-nowrap shrink-0">💸 + Payout</button>
            <button type="button" onClick={handleMyProfile} className="flex items-center gap-2 py-2.5 px-4 rounded-lg bg-slate-600 text-white text-sm font-semibold border-none cursor-pointer hover:bg-slate-700 whitespace-nowrap shrink-0">👤 My Profile</button>
            <button type="button" onClick={handleLogoutClick} className="flex items-center gap-2 py-2.5 px-4 rounded-lg bg-red-600 text-white text-sm font-semibold border-none cursor-pointer hover:bg-red-700 whitespace-nowrap shrink-0">🚪 Logout</button>
          </div>
        </div>

        <div className="mb-6">
          <h2 className="text-xl font-bold text-slate-800 m-0 mb-4">My Account</h2>

          <div className="space-y-6">
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h3 className="text-base font-bold text-slate-800 m-0 mb-4 pb-2 border-b-2 border-slate-200">Profile Details</h3>
              <div
                className="grid grid-cols-1 gap-3"
                style={isDesktop ? { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' } : undefined}
              >
                {[
                  { label: 'Name:', value: accountData.name },
                  { label: 'Mobile:', value: accountData.mobile },
                  { label: 'Email:', value: accountData.email },
                  { label: 'Business Name:', value: accountData.businessName },
                  { label: 'App URL:', value: accountData.appUrl },
                  { label: 'Accepting Payments:', value: accountData.acceptingPayments },
                  { label: 'IP Address:', value: accountData.ipAddress },
                  { label: 'Business Type:', value: accountData.businessType },
                  { label: 'Services:', value: accountData.services },
                  { label: 'Register On:', value: accountData.registerDate },
                ].map((row, i) => (
                  <div key={i} className="flex flex-wrap items-baseline gap-2 py-1 min-w-0">
                    <span className="text-sm font-semibold text-slate-600 min-w-[140px] shrink-0">{row.label}</span>
                    <span className="text-sm text-slate-800 break-all min-w-0">{row.value}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4 pb-2 border-b-2 border-slate-200">
                <h3 className="text-base font-bold text-slate-800 m-0">API Keys</h3>
                <button
                  type="button"
                  onClick={handleSyncApiKeysFromManager}
                  disabled={apiKeysSyncLoading}
                  className="py-2 px-4 rounded-lg bg-violet-600 text-white text-sm font-semibold border-none cursor-pointer hover:bg-violet-700 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {apiKeysSyncLoading ? 'Updating...' : 'Update'}
                </button>
              </div>
              {apiKeysSyncMessage && (
                <div className={`mb-4 p-3 rounded-lg text-sm ${apiKeysSyncMessage.type === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                  {apiKeysSyncMessage.text}
                </div>
              )}
              <div className="space-y-3">
                {[
                  { label: 'Client ID:', value: accountData.apiId },
                  { label: 'API Key:', value: accountData.apiKey },
                  { label: 'Client Secret:', value: accountData.secretKey },
                  { label: 'Webhook Url:', value: accountData.webhookUrl },
                ].map((row, i) => (
                  <div key={i} className="flex flex-wrap items-center gap-2 py-2 border-b border-slate-100 last:border-0">
                    <span className="text-sm font-semibold text-slate-600 min-w-[120px]">{row.label}</span>
                    <span className={`text-sm text-slate-800 break-all font-mono flex-1 ${row.label.includes('Secret') ? 'text-amber-700' : ''}`}>{row.value}</span>
                    <button type="button" className="p-2 rounded-lg border border-slate-200 hover:bg-slate-100 text-sm cursor-pointer" title="Copy">📋</button>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h3 className="text-base font-bold text-slate-800 m-0 mb-4 pb-2 border-b-2 border-slate-200">Reset Password</h3>
              <p className="text-sm text-slate-600 mb-4">Maximum 3 password resets per day.</p>
              <form onSubmit={handleResetPassword} className="space-y-4 max-w-md">
                {passwordMessage && (
                  <div className={`p-3 rounded-lg text-sm ${passwordMessage.type === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                    {passwordMessage.text}
                  </div>
                )}
                <div>
                  <label htmlFor="currentPassword" className="block text-sm font-semibold text-slate-700 mb-1">Current Password</label>
                  <input
                    id="currentPassword"
                    type="password"
                    value={passwordForm.currentPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                    className="w-full py-2 px-3 border border-slate-200 rounded-lg text-slate-800 text-sm focus:border-violet-500 focus:ring-1 focus:ring-violet-500"
                    placeholder="Enter current password"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="newPassword" className="block text-sm font-semibold text-slate-700 mb-1">New Password</label>
                  <input
                    id="newPassword"
                    type="password"
                    value={passwordForm.newPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                    className="w-full py-2 px-3 border border-slate-200 rounded-lg text-slate-800 text-sm focus:border-violet-500 focus:ring-1 focus:ring-violet-500"
                    placeholder="Enter new password (min 6 characters)"
                    required
                    minLength={6}
                  />
                </div>
                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-semibold text-slate-700 mb-1">Confirm New Password</label>
                  <input
                    id="confirmPassword"
                    type="password"
                    value={passwordForm.confirmPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                    className="w-full py-2 px-3 border border-slate-200 rounded-lg text-slate-800 text-sm focus:border-violet-500 focus:ring-1 focus:ring-violet-500"
                    placeholder="Confirm new password"
                    required
                  />
                </div>
                <button
                  type="submit"
                  disabled={passwordLoading}
                  className="py-2.5 px-4 rounded-lg bg-violet-600 text-white text-sm font-semibold border-none cursor-pointer hover:bg-violet-700 disabled:opacity-60"
                >
                  {passwordLoading ? 'Updating...' : '🔐 Reset Password'}
                </button>
              </form>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h3 className="text-base font-bold text-slate-800 m-0 mb-4 pb-2 border-b-2 border-slate-200">Reset TPIN</h3>
              <p className="text-sm text-slate-600 mb-4">
                Generate a new 6-digit TPIN for payouts. The new TPIN will be sent to your registered mobile number (****{accountData.mobile ? String(accountData.mobile).replace(/\D/g, '').slice(-4) : '****'}) and is valid for 15 days.
              </p>
              {tpinMessage && (
                <div className={`p-3 rounded-lg text-sm mb-4 ${tpinMessage.type === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                  {tpinMessage.text}
                </div>
              )}
              <button
                type="button"
                onClick={handleResetTpin}
                disabled={tpinLoading}
                className="py-2.5 px-4 rounded-lg bg-violet-600 text-white text-sm font-semibold border-none cursor-pointer hover:bg-violet-700 disabled:opacity-60"
              >
                {tpinLoading ? 'Sending...' : '🔑 Reset TPIN'}
              </button>
            </div>
          </div>
        </div>
      </main>

      {/* Logout Modal */}
      <LogoutModal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={handleLogout}
        userName={user?.name}
        userRole={user?.role}
      />

      {/* Wallet Credits Modal */}
      <WalletCreditsModal
        isOpen={showCreditsModal}
        onClose={() => setShowCreditsModal(false)}
      />

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default MerchantAccount;
